import { createTheme } from '@mui/material/styles';
import {
  BORDER, BRAND, BRAND_HOVER, BRAND_TEXT, DURATION, EASE, ELEVATION_OVERLAY, FONT_FAMILY, RADIUS, STATUS_TEXT, SURFACE, TEXT,
} from '../../styles/tokens';

function blendWith(hex: string, target: number, weight: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const c = (ch: number) => Math.round(ch * weight + target * (1 - weight));
  return `#${[c(r), c(g), c(b)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

export function lightenHex(hex: string, amount: number) {
  return blendWith(hex, 255, amount);
}

export function darkenHex(hex: string, amount: number) {
  return blendWith(hex, 0, amount);
}

export function applyBrandCssVars(hex: string | null) {
  // Must match createBerryTheme's own default below — otherwise every MUI
  // component (using the theme's primary color) renders a different colour than
  // every plain-CSS element (using this variable) whenever an org hasn't set
  // a custom brand color, which is the out-of-the-box state.
  const color = hex || BRAND;
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  const dark = darkenHex(color, 0.85);
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', color);
  root.style.setProperty('--brand-primary-dark', dark);
  root.style.setProperty('--brand-primary-a008', `rgba(${r},${g},${b},0.08)`);
  root.style.setProperty('--brand-primary-a015', `rgba(${r},${g},${b},0.15)`);
  root.style.setProperty('--brand-primary-a018', `rgba(${r},${g},${b},0.18)`);
  root.style.setProperty('--brand-primary-a020', `rgba(${r},${g},${b},0.20)`);
  root.style.setProperty('--brand-primary-a030', `rgba(${r},${g},${b},0.30)`);
  root.style.setProperty('--sidebar-active', color);
  root.style.setProperty('--sidebar-active-bg', `rgba(${r},${g},${b},0.15)`);
  root.style.setProperty('--sidebar-active-text', lightenHex(color, 0.55));
}

export function applySidebarCssVars(hex: string | null) {
  const color = hex || '#131313';
  const elevated = lightenHex(color, 0.12);
  const root = document.documentElement;
  root.style.setProperty('--sidebar-bg', color);
  root.style.setProperty('--sidebar-bg-elevated', elevated);
}

const fast = `${DURATION.fast}ms ${EASE.standard}`;
const v = (name: string) => `var(${name})`;

/**
 * The MUI theme, aligned to src/styles/tokens.css (DESIGN_SYSTEM.md "Foundations").
 * Palette values must be real colours (MUI computes shades from them), so they come
 * from tokens.ts; component styles use the CSS variables so they follow the tokens.
 */
export default function createBerryTheme(brandColor?: string | null) {
  const primary = brandColor || BRAND;
  const primaryLight = lightenHex(primary, 0.7);
  const primaryDark = brandColor ? darkenHex(primary, 0.82) : BRAND_HOVER;

  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        light: primaryLight,
        main: primary,
        dark: primaryDark,
        contrastText: '#ffffff',
      },
      // The magenta accent is retired (DESIGN_SYSTEM.md): secondary is a neutral
      // slate, so nothing that asks for it brings a second brand colour back.
      secondary: {
        light: '#64748b',
        main: '#334155',
        dark: '#1d293d',
        contrastText: '#ffffff',
      },
      success: { main: '#22c55e', dark: STATUS_TEXT.success },
      warning: { main: '#f59e0b', dark: STATUS_TEXT.warning },
      // #ef4444 is 3.76:1 on white – under the 4.5 floor. This is the same hue,
      // dark enough to read.
      error: { main: '#C62828', dark: STATUS_TEXT.danger },
      background: {
        default: SURFACE.page,
        paper: SURFACE.card,
      },
      divider: BORDER.default,
      text: {
        primary: TEXT.primary,
        secondary: TEXT.secondary,
      },
    },
    shape: {
      borderRadius: RADIUS.sm,
    },
    transitions: {
      duration: {
        shortest: DURATION.instant,
        shorter: DURATION.fast,
        short: DURATION.base,
        standard: DURATION.slow,
        complex: DURATION.slow,
        enteringScreen: DURATION.slow,
        leavingScreen: Math.round(DURATION.slow * 0.75),
      },
      easing: {
        easeInOut: EASE.standard,
        easeOut: EASE.enter,
        easeIn: EASE.exit,
        sharp: EASE.standard,
      },
    },
    typography: {
      fontFamily: FONT_FAMILY,
      fontWeightLight: 400,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 500,
      h1: { fontSize: '1.375rem', lineHeight: '30px', fontWeight: 500, letterSpacing: '-0.015em' },
      h2: { fontSize: '1.375rem', lineHeight: '30px', fontWeight: 500, letterSpacing: '-0.015em' },
      h3: { fontSize: '1.125rem', lineHeight: '26px', fontWeight: 500, letterSpacing: '-0.01em' },
      h4: { fontSize: '1rem', lineHeight: '24px', fontWeight: 500, letterSpacing: '-0.005em' },
      h5: { fontSize: '0.875rem', lineHeight: '21px', fontWeight: 500 },
      h6: { fontSize: '0.875rem', lineHeight: '21px', fontWeight: 500 },
      subtitle1: { fontSize: '0.875rem', fontWeight: 500 },
      subtitle2: { fontSize: '0.75rem', fontWeight: 400 },
      caption: { fontSize: '0.75rem', lineHeight: '16px', fontWeight: 400 },
      body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: '1.5' },
      body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: '1.5' },
      button: { fontSize: '0.875rem', textTransform: 'none', fontWeight: 500, letterSpacing: '0' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, #root': { fontFamily: v('--font-family-body') },
          body: { fontFamily: v('--font-family-body'), fontWeight: 400 },
          'button, input, textarea, select': { fontFamily: 'inherit' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: '1px solid rgba(255, 255, 255, 0.07)',
            background: 'var(--sidebar-bg)',
            boxShadow: 'none',
          },
          // A right-side detail drawer (spec 4.13): white, rounded on its open edge
          paperAnchorRight: {
            background: v('--color-surface-card'),
            borderRight: 'none',
            borderRadius: `${RADIUS.tile}px 0 0 ${RADIUS.tile}px`,
            boxShadow: ELEVATION_OVERLAY,
          },
          paperAnchorBottom: {
            background: v('--color-surface-card'),
            borderRadius: `${RADIUS.tile}px ${RADIUS.tile}px 0 0`,
            boxShadow: ELEVATION_OVERLAY,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: v('--color-backdrop') } },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: `1px solid ${v('--color-border')}`,
            background: v('--color-surface-page'),
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: { minHeight: '48px !important' },
        },
      },
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: RADIUS.sm,
            paddingInline: 16,
            minHeight: 40,
            boxShadow: 'none',
            fontWeight: 500,
            borderWidth: 1,
            transition: `background-color ${fast}, border-color ${fast}, color ${fast}, transform ${DURATION.instant}ms ${EASE.standard}`,
            '&:hover': { boxShadow: 'none' },
            '&:active': { transform: 'scale(0.98)' },
            '&.Mui-disabled': { opacity: 0.4 },
          },
          sizeSmall: { minHeight: 36, paddingInline: 12 },
          sizeLarge: { minHeight: 44, paddingInline: 20 },
          // A trailing arrow on "go somewhere" buttons nudges 2px right on hover
          endIcon: { transition: `transform ${fast}` },
        },
        variants: [
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              color: '#ffffff',
              backgroundColor: primary,
              '&:hover': { backgroundColor: primaryDark, boxShadow: 'none' },
              '&.Mui-disabled': { color: '#ffffff', backgroundColor: primary },
            },
          },
          {
            props: { variant: 'contained', color: 'secondary' },
            style: { color: '#ffffff', '&:hover': { backgroundColor: '#1d293d' } },
          },
          {
            // Destructive: inside confirm dialogs only
            props: { variant: 'contained', color: 'error' },
            style: {
              color: '#ffffff',
              backgroundColor: v('--color-danger-text'),
              '&:hover': { backgroundColor: v('--color-danger-text') },
            },
          },
          { props: { variant: 'contained', color: 'success' }, style: { color: '#ffffff' } },
          { props: { variant: 'contained', color: 'warning' }, style: { color: '#ffffff' } },
          {
            // Secondary: white, Cool Grey border, black text
            props: { variant: 'outlined' },
            style: {
              borderWidth: 1,
              color: v('--color-text-primary'),
              borderColor: v('--color-border-strong'),
              backgroundColor: v('--color-surface-card'),
              '&:hover': {
                borderWidth: 1,
                borderColor: v('--color-border-strong'),
                backgroundColor: v('--color-surface-subtle'),
              },
            },
          },
          {
            props: { variant: 'outlined', color: 'error' },
            style: { color: v('--color-danger-text'), borderColor: v('--color-danger-text') },
          },
          {
            // Ghost: purple text, a tint on hover
            props: { variant: 'text' },
            style: {
              color: v('--color-brand-text'),
              '&:hover': { backgroundColor: v('--color-brand-tint') },
            },
          },
          {
            props: { variant: 'text', color: 'inherit' },
            style: { color: 'inherit', '&:hover': { backgroundColor: v('--color-surface-subtle') } },
          },
          {
            props: { variant: 'text', color: 'error' },
            style: { color: v('--color-danger-text'), '&:hover': { backgroundColor: v('--color-danger-tint') } },
          },
        ],
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: v('--color-text-secondary'),
            borderRadius: RADIUS.sm,
            minWidth: 24,
            minHeight: 24,
            transition: `background-color ${fast}, color ${fast}`,
            '&:hover': { background: v('--color-surface-subtle') },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': { backgroundColor: 'transparent' },
            '&.Mui-selected:hover': { backgroundColor: 'transparent' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.card,
            border: `1px solid ${v('--color-border')}`,
            boxShadow: 'none',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.pill,
            fontSize: '0.75rem',
            fontWeight: 500,
            height: 26,
          },
          sizeSmall: { height: 22 },
          filled: {
            '&.MuiChip-colorDefault': {
              backgroundColor: v('--color-surface-subtle'),
              color: v('--color-text-secondary'),
            },
            '&.MuiChip-colorPrimary': {
              backgroundColor: v('--color-brand-tint-strong'),
              color: v('--color-brand-text'),
            },
          },
          outlined: { borderColor: v('--color-border') },
          clickable: { '&:hover': { borderColor: v('--color-border-strong') } },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', borderColor: v('--color-border') },
        },
      },
      // Fields (spec 4.8): 40px, Cool Grey input border, black hover, purple focus with the halo
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.sm,
            backgroundColor: v('--color-surface-card'),
            fontSize: '0.875rem',
            transition: `box-shadow ${fast}`,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: v('--color-border-input'),
              transition: `border-color ${fast}`,
            },
            '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: v('--color-border-input-hover'),
            },
            '&.Mui-focused': { boxShadow: v('--focus-ring-halo') },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: v('--color-brand'),
              borderWidth: 2,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: v('--color-danger-text') },
          },
          input: { paddingBlock: 9.5, paddingInline: 12 },
          inputSizeSmall: { paddingBlock: 7.5 },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            color: v('--color-text-muted'),
            '&.Mui-focused': { color: v('--color-brand-text') },
            '&.Mui-error': { color: v('--color-danger-text') },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: { fontSize: '0.8125rem', fontWeight: 500, color: v('--color-text-primary') },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
            marginInline: 0,
            marginTop: 6,
            color: v('--color-text-muted'),
            '&.Mui-error': { color: v('--color-danger-text') },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: { width: 52, height: 36, padding: 8 },
          switchBase: {
            padding: 10,
            '&.Mui-checked': { transform: 'translateX(16px)', color: '#ffffff' },
            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: v('--color-brand'), opacity: 1 },
          },
          thumb: { width: 16, height: 16, boxShadow: 'none', color: '#ffffff' },
          // size="small": a 32 x 16 track with a 12px thumb, 2px clear all round. MUI's own
          // small padding (4px on the base) met the 16px thumb and pushed it past the track.
          sizeSmall: {
            width: 40,
            height: 24,
            padding: 4,
            '& .MuiSwitch-switchBase': { padding: 6 },
            '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(16px)' },
            '& .MuiSwitch-thumb': { width: 12, height: 12 },
          },
          track: {
            borderRadius: RADIUS.pill,
            backgroundColor: v('--color-border-strong'),
            opacity: 1,
            transition: `background-color ${fast}`,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: {
            height: 2,
            backgroundColor: v('--color-brand'),
            transition: `all ${DURATION.base}ms ${EASE.standard}`,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 40,
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 400,
            color: v('--color-text-muted'),
            paddingInline: 12,
            '&.Mui-selected': { color: v('--color-text-primary'), fontWeight: 500 },
            '&.Mui-focusVisible': { outlineOffset: -2 },
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: false },
        styleOverrides: {
          tooltip: {
            backgroundColor: v('--color-surface-inverse'),
            color: v('--color-text-on-inverse'),
            fontSize: '0.75rem',
            lineHeight: '16px',
            fontWeight: 400,
            borderRadius: RADIUS.sm,
            padding: '8px 10px',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          list: { padding: 8 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: 36,
            borderRadius: RADIUS.sm,
            fontSize: '0.875rem',
            '&:hover': { backgroundColor: v('--color-surface-subtle') },
            '&.Mui-selected': { backgroundColor: v('--color-brand-tint') },
            '&.Mui-selected:hover': { backgroundColor: v('--color-brand-tint') },
            '&.Mui-focusVisible': { outlineOffset: -2, boxShadow: 'none', backgroundColor: v('--color-surface-subtle') },
          },
        },
      },
      // Modal (spec 4.13): 16px radius, overlay shadow, 20/24 header, 1px-bordered footer
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: RADIUS.tile, boxShadow: ELEVATION_OVERLAY, maxHeight: 'calc(100% - 64px)' },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { padding: '20px 24px 0', fontSize: '1rem', lineHeight: '24px', fontWeight: 500 },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: '16px 24px 24px' },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: '16px 24px', gap: 8, borderTop: `1px solid ${v('--color-border')}`, '& > :not(style) ~ :not(style)': { marginLeft: 0 } },
        },
      },
      // Snackbar and Alert-in-snackbar: the inverse toast (spec 4.13)
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            backgroundColor: v('--color-surface-inverse'),
            color: v('--color-text-on-inverse'),
            borderRadius: RADIUS.md,
            fontSize: '0.875rem',
            maxWidth: 360,
            boxShadow: ELEVATION_OVERLAY,
          },
          action: { color: v('--color-brand-on-inverse') },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: RADIUS.card, fontSize: '0.875rem' },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: {
          root: { color: BRAND_TEXT, textUnderlineOffset: 2 },
        },
      },
    },
  });
}
